import { List } from 'immutable'
import { cn } from '@appfluence/classnames'
import _ from 'lodash'
import { useCallback, useMemo } from 'react'
import { itemHelper } from '../../common/src/helpers'
import { addHours, addDays } from 'date-fns'
import { useSelectedItemId } from '../../hooks/PMHooks'
import { Item, ProjectList, ItemList } from '@/types'
import {
  CalendarProjectInfoMap,
  FullCalendarProps,
  ColorPaletteMap,
  DefaultCalendarColorName,
  View,
  FullCalendarRef,
  PMEvent,
} from './PMCalendar.Common'
import { useSelector } from 'react-redux'
import { getCalendarColorMapping } from '@/selectors/uiSelectors'
import { isCalendarMonthHotKey, useHotkey, isCalendarDayHotKey, isCalendarWeekHotKey } from '@/hooks/useHotkey'
import { getAvatarURIForEmail } from '@/common/src/constants'

const mapItem =
  (projectInfoMap: CalendarProjectInfoMap, selectedItemId: number) =>
  (item?: Item): PMEvent => {
    const id = itemHelper.getId(item)
    let end = itemHelper.getDueDate(item)
    const projectIdd = itemHelper.getProjectIdd(item)
    const colorName = projectInfoMap.get(projectIdd)?.color ?? DefaultCalendarColorName
    const projectColor = ColorPaletteMap[colorName]

    if (!end) {
      //todo catch
      throw new Error('dueDate must exist')
    }
    const start = itemHelper.getStartTimestamp(item) ? itemHelper.getStartDate(item) : addHours(end, -1)
    const allDay = itemHelper.getAllDay(item)
    if (allDay) {
      end = addDays(end, 1)
    }

    return {
      title: itemHelper.getName(item),
      id: itemHelper.getId(item),
      start,
      end,
      allDay: itemHelper.getAllDay(item),
      pmItem: item!,
      backgroundColor: projectColor?.colorBg,
      textColor: projectColor?.fontColor,
      className: 'pm-event',
      classNames: id === selectedItemId ? 'selected' : '',
      borderColor: projectColor?.colorBg,
    }
  }

interface IUsePMDataProps {
  items: ItemList
  projects: ProjectList
}

const itemHasDueDate = (item?: Item) => !!itemHelper.getDueDate(item)

export const usePMData = ({ items: itemsInModel, projects: projectsInModel }: IUsePMDataProps) => {
  const projectInfoMap = useSelector(getCalendarColorMapping)
  const loading = !projectsInModel || !itemsInModel
  const items = useMemo(() => itemsInModel ?? List(), [itemsInModel])
  const selectedItemId = useSelectedItemId()
  const events = useMemo(
    () =>
      items
        .filter(itemHasDueDate)
        .filter(i => {
          const idd = itemHelper.getProjectIdd(i)
          return !!projectInfoMap.get(idd)?.display
        })
        .map(mapItem(projectInfoMap, selectedItemId))
        .toArray(),
    [items, projectInfoMap, selectedItemId]
  )
  return {
    events,
    projectInfoMap,
    loading,
  }
}

const formatStartTime = (d: Date) => d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })

export const renderEvent: FullCalendarProps['eventContent'] = eventData => {
  const item = eventData.event.extendedProps.pmItem as Item
  const username = itemHelper.getOwnerUsername(item)
  const done = itemHelper.isCompleted(item)
  const avatarSrc = getAvatarURIForEmail(username) ?? undefined
  const { start, title, allDay } = eventData.event
  const projectColor = eventData.backgroundColor
  const timeText = start ? formatStartTime(start) : ''
  const timeCn = cn('Time', {
    hidden: allDay,
  })
  return (
    <div className="Event" title={title}>
      <img
        className="Avatar"
        src={avatarSrc}
        alt={username}
        style={{
          borderColor: projectColor ?? '#FFFFFF',
        }}
      />
      <span className={timeCn}>{timeText}</span>
      <span className={cn('Title truncate', done && 'line-through')}>{title}</span>
    </div>
  )
}

type DateTuple = [Date, Date]
export const adaptAllDayEventDatesToPM = ([start, end]: DateTuple): DateTuple => [
  addHours(start, 12),
  addHours(end, -12),
]

export const useKeyboardShortcuts = (ref: FullCalendarRef) => {
  const handleMonth = useCallback(() => ref.current?.getApi().changeView(View.Month), [ref])
  const handleWeek = useCallback(() => ref.current?.getApi().changeView(View.Week), [ref])
  const handleDay = useCallback(() => ref.current?.getApi().changeView(View.Day), [ref])
  useHotkey(isCalendarMonthHotKey, handleMonth, true)
  useHotkey(isCalendarWeekHotKey, handleWeek, true)
  useHotkey(isCalendarDayHotKey, handleDay, true)
}
